import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [statsRes, projectsRes] = await Promise.all([
        fetch('/api/projects/stats'),
        fetch('/api/projects')
      ]);

      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData);
      }

      if (projectsRes.ok) {
        const projectsData = await projectsRes.json();
        setRecentProjects(projectsData.slice(0, 3)); // Get 3 most recent
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h2 className="text-4xl font-bold text-gray-900 mb-2">Welcome to Sticker Maker Admin</h2>
        <p className="text-lg text-gray-600">Create amazing AI-powered sticker designs ✨</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* New Project Card */}
        <Link to="/new" className="card hover:shadow-md transition-shadow">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">New Sticker Project</h3>
              <p className="text-gray-600 text-sm">Start creating a new set of AI-generated stickers</p>
            </div>
          </div>
        </Link>

        {/* Recent Projects Card */}
        <Link to="/projects" className="card hover:shadow-md transition-shadow">
          <div className="flex items-start space-x-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
              </svg>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Recent Projects</h3>
              <p className="text-gray-600 text-sm">View and edit your existing sticker projects</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Beautiful Stats Cards */}
      {stats && !loading && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card bg-gradient-to-br from-blue-50 to-indigo-100 border-2 border-indigo-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Total Projects</p>
                <p className="text-4xl font-bold text-indigo-600">{stats.totalProjects}</p>
              </div>
              <div className="text-5xl">📁</div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-green-50 to-emerald-100 border-2 border-green-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Stickers Created</p>
                <p className="text-4xl font-bold text-green-600">{stats.totalStickers}</p>
              </div>
              <div className="text-5xl">✨</div>
            </div>
          </div>

          <div className="card bg-gradient-to-br from-purple-50 to-pink-100 border-2 border-purple-200">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 mb-1">Completed</p>
                <p className="text-4xl font-bold text-purple-600">{stats.completedProjects}</p>
              </div>
              <div className="text-5xl">🎉</div>
            </div>
          </div>
        </div>
      )}

      {/* Recent Projects */}
      {recentProjects.length > 0 && (
        <div className="mb-8">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-2xl font-bold text-gray-900">Recent Projects</h3>
            <Link to="/projects" className="text-indigo-600 hover:text-indigo-800 font-medium">
              View All →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {recentProjects.map((project) => (
              <Link
                key={project.id}
                to={`/projects/${project.id}`}
                className="card hover:shadow-lg transition-shadow overflow-hidden"
              >
                {/* Preview Grid */}
                {project.preview_stickers && project.preview_stickers.length > 0 ? (
                  <div className="grid grid-cols-2 gap-2 mb-4 bg-gray-50 p-3 rounded -m-4 mb-4">
                    {[0, 1, 2, 3].map((index) => (
                      <div
                        key={index}
                        className="aspect-square bg-white rounded border-2 border-gray-200 overflow-hidden"
                      >
                        {project.preview_stickers[index] ? (
                          <img
                            src={`data:image/png;base64,${project.preview_stickers[index].image_data}`}
                            alt={`Sticker ${index + 1}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-gray-300 text-2xl">
                            ?
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="mb-4 bg-gray-50 p-8 rounded -m-4 mb-4 flex items-center justify-center">
                    <p className="text-gray-400 text-sm">No stickers yet</p>
                  </div>
                )}

                <h4 className="text-lg font-semibold text-gray-900 mb-2">{project.theme}</h4>
                <div className="flex items-center justify-between text-sm text-gray-600">
                  <span>{project.sticker_count || 0} stickers</span>
                  <span className={`px-2 py-1 rounded text-xs ${
                    project.status === 'completed' ? 'bg-green-100 text-green-800' :
                    project.status === 'generating' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {project.status}
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
